/* TODO: t() */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { View, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';

import { createHexId } from '../../support/createHexId';
import { APP_FIELDS_BY_ID } from '../../support/shared/constants/Constants';
import FormBuilderActions from '../../support/FormBuilderActions';
import FieldGroupEditor from './FieldGroupEditor';
import FieldGroup from './FieldGroup';
import renderElementMenuItem, {
  formElementTypes,
} from './FormElements/helpers/renderElementMenuItem';
import ExampleLink from '../ExampleLink';

import form from './FormElements/helpers/listForm';
import getFieldSets from './helpers/getFieldSets';
import reorderByIndex from '../../support/helpers/reorderByIndex';
import toElement from '../../support/helpers/toElement';

import {
  Hoverable,
  Text,
  IconWithText,
  Button,
  ButtonToolbar,
  DropdownButton,
} from '../uiold';
import { themeColors } from '../uiold/Constants';
import ItemForm from '../ItemForm';
import MasterDetailView from '../uiold/MasterDetailView';
import SortableList from '../uiold/SortableList';
import Checkbox from '../uiold/CheckboxOld';
import IndexedList from '../../support/shared/IndexedList';
import createFormElement from './FormElements/helpers/createFormElement';

import SignupFormModel from './SignupFormModel';
import { FormField } from './FormElements/types/FormField';
import { Market } from '../../types/shared/Market';
import { Addon } from '../../types/shared/Addon';
import { Group } from '../../types/shared/Group';

type Props = {
  accountID: string;
  seasonID: string | undefined;
  formDef: SignupFormModel;
  markets: IndexedList<Market> | null | undefined;
  addons: Map<string, Addon> | null | undefined;
  isAddingGroup?: boolean;
  onSaveGroup: (group: Group) => void;
  onRemoveGroup: (group: Group) => void;
  onEditGroupCancel: () => void;
  onCreateGroupStart: () => void;
  onAddFieldPress: (fieldID: string) => void;
};

type State = {
  selectedFieldID: string | null | undefined;
  editingGroup: Partial<Group> | null | undefined;
};

const EMPTY_GROUPS: Array<Group> = [];

class FieldsEditor extends Component<Props, State> {
  _scrollView = React.createRef<ScrollView>();

  constructor(props: Props) {
    super(props);
    this.state = {
      // this is when a field is selected for editing
      selectedFieldID: null,
      editingGroup: null,
    };
  }

  componentDidMount() {
    document.addEventListener('click', this._documentClick);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this._documentClick);
  }

  componentDidUpdate(oldProps: Props) {
    let newProps = this.props;
    let newFields = newProps.formDef.fields;
    let oldFields = oldProps.formDef.fields;
    if (
      newFields.length > oldFields.length &&
      newFields.last() !== oldFields.last()
    ) {
      // A field was added to the bottom of the list; Scroll to the end.
      let scrollView = this._scrollView.current;
      if (scrollView) {
        let scrollableNode = scrollView.getScrollableNode();
        let innerViewNode = scrollView.getInnerViewNode();
        if (scrollableNode && innerViewNode) {
          let scrollableHeight = scrollableNode.getBoundingClientRect().height;
          let contentHeight = innerViewNode.getBoundingClientRect().height;
          if (contentHeight > scrollableHeight) {
            scrollView.scrollTo({ x: 0, y: contentHeight - scrollableHeight });
          }
        }
      }
    }
    if (newProps.isAddingGroup === true && oldProps.isAddingGroup === false) {
      this.setState({ editingGroup: createNewGroup() });
      this._closeEditor();
    }
  }

  render() {
    return (
      <MasterDetailView detailView={this._renderEditView()}>
        <ScrollView ref={this._scrollView}>
          {this._renderFieldListView()}
        </ScrollView>
      </MasterDetailView>
    );
  }

  _renderFieldListView = () => {
    let { groups, fields } = this.props.formDef;
    if (fields.length === 0) {
      return (
        <View style={styles.root}>
          <Text>No fields in this form.</Text>
        </View>
      );
    }

    let fieldSets = getFieldSets(groups || EMPTY_GROUPS, fields.toArray());
    let items = fieldSets.map((fieldSet) => {
      let { group, children } = fieldSet;
      let isSelect = children.some(
        (field) => field.id === this.state.selectedFieldID,
      );
      if (group == null) {
        return this._renderField(children[0] as FormField, false);
      }
      return (
        <FieldGroup
          isSelect={isSelect}
          key={group.id}
          group={group}
          onRemove={this._removeGroup}
          onSelect={this._startEditGroup}
          items={children.map((field) => this._renderField(field, true))}
        />
      );
    });
    return (
      <SortableList
        items={items}
        onSort={this._onOrderChange}
        {...{
          shouldCancelStart: this._shouldCancelSort,
          onSortStart: this._onSortStart,
        }}
      />
    );
  };
  _renderField = (field: FormField, isGrouped: boolean) => {
    let { markets, addons } = this.props;
    let { definition } = field;
    let { selectedFieldID } = this.state;
    let isSelect = selectedFieldID === field.id;
    let isHidden =
      typeof definition.hidden === 'boolean' ? definition.hidden : false;

    return (
      <Hoverable
        key={field.id}
        render={(isHovered, eventHandlers) => (
          /* TODO: We have one too many nested <View> here */
          <View
            key={field.id}
            {...eventHandlers}
            style={[
              styles.sortableElement,
              isHovered && styles.fieldHovered,
              isSelect && styles.selectedField,
              isHidden && styles.isHidden,
            ]}
            onClick={(event) => {
              eventHandlers.onMouseLeave(event);
              this._onFieldClick(field.id);
            }}
          >
            {isGrouped ? null : this._renderCheckbox(field)}
            <View style={styles.defaultSortable}>
              {field.renderFieldView({ markets, addons })}
              <View style={styles.overlay} />
              {(isHovered || isSelect) && this._renderFieldToolbar(field)}
            </View>
          </View>
        )}
      />
    );
  };

  _renderCheckbox = (field: FormField) => {
    if (this.props.isAddingGroup) {
      return (
        <View style={styles.groupCheckBox}>
          <Checkbox onChange={() => this._addFieldToGroup(field)} />
        </View>
      );
    }
    return null;
  };

  _renderFieldToolbar = (field: FormField) => {
    if (this.props.isAddingGroup) {
      return;
    }
    return (
      <ButtonToolbar style={styles.toolbarContainer}>
        {this._renderDropdownMenu(field)}
        <Button style={styles.moveButton} title="" iconName="move" />
      </ButtonToolbar>
    );
  };

  _renderDropdownMenu = (field: FormField) => {
    let dropdownMenuItems = this._renderDropdownMenuItems(field);
    if (dropdownMenuItems.length) {
      return (
        <DropdownButton
          icon="optionHoriz"
          alignPickerRight
          noBorder
          style={{ backgroundColor: 'transparent' }}
          onShow={() => {
            this._onFieldClick(field.id);
          }}
        >
          {dropdownMenuItems}
        </DropdownButton>
      );
    }
  };

  _renderDropdownMenuItems = (field: FormField) => {
    let { onAddFieldPress } = this.props;
    let items = [];
    // don't allow delete for fields that are necessary to the app
    if (!(field.id in APP_FIELDS_BY_ID)) {
      items.push(
        <Button
          style={styles.deleteButton}
          title="Delete"
          iconName="remove"
          key="0"
          onPress={() => {
            this._removeField(field);
          }}
        />,
      );
    }
    items.push(
      <Button
        iconName="addBox"
        title="Insert Field Below"
        key="1"
        onPress={() => {
          onAddFieldPress(field.id);
        }}
        noBorder
      />,
    );
    return items;
  };

  _renderEditView = () => {
    let { isAddingGroup } = this.props;
    let { editingGroup } = this.state;
    return (
      <View style={styles.root}>
        {isAddingGroup || editingGroup
          ? this._renderEditGroupView()
          : this._renderEditFieldView()}
      </View>
    );
  };

  _renderEditGroupView = () => {
    let { editingGroup } = this.state;
    if (!editingGroup) {
      return;
    }
    return (
      <FieldGroupEditor
        group={editingGroup as Group}
        formDef={this.props.formDef}
        onChange={(group) => this.setState({ editingGroup: group })}
        onSave={this._onGroupSave}
        onCancel={this._onGroupCancel}
      />
    );
  };

  _renderEditFieldView = () => {
    let { formDef, addons } = this.props;
    let isEmpty = formDef.fields.length === 0;
    let selectedField = this._getSelectedField();
    return (
      <View style={styles.root}>
        {selectedField ? (
          <ItemForm style={styles.fieldContainer}>
            <ItemForm.Header noline style={styles.fieldHeader}>
              <TouchableOpacity activeOpacity={0.6} onPress={this._onBackPress}>
                {form[selectedField.type as keyof typeof form] && (
                  <IconWithText
                    iconName="arrowBack"
                    text={form[
                      selectedField.type as keyof typeof form
                    ].getDescription()}
                  />
                )}
              </TouchableOpacity>
            </ItemForm.Header>
            <ItemForm.Body>
              {selectedField.renderFieldEditor({ addons })}
            </ItemForm.Body>
            <ItemForm.Footer style={styles.itemFooter}>
              {this._renderEditorControls(selectedField)}
            </ItemForm.Footer>
          </ItemForm>
        ) : !isEmpty ? (
          <View style={styles.formItemContainer}>
            <View style={styles.formActionContainer}>
              <Text style={styles.addFormText}>Add Form Elements</Text>
            </View>
            {this._renderAddFieldMenuItems()}
            <View style={styles.formActionContainer}>
              <Text style={styles.addFormText}>Form Editor</Text>
            </View>
            <View style={styles.formActionContainer}>
              <TouchableOpacity
                activeOpacity={0.6}
                onPress={this._onCreateGroup}
                style={styles.padding}
              >
                <IconWithText iconName="group" text="Create Group" />
              </TouchableOpacity>
            </View>
            <View style={styles.formActionContainer}>
              <Text style={styles.addFormText}>Signup Form Example</Text>
            </View>
            <View style={styles.formActionContainer}>
              <ExampleLink
                resourceID="forms/signup"
                accountID={this.props.accountID}
                seasonID={this.props.seasonID}
              />
            </View>
          </View>
        ) : null}
      </View>
    );
  };

  _renderEditorControls = (field: FormField) => {
    let deleteButton;
    if (
      !(field.id in APP_FIELDS_BY_ID) &&
      field.definition.type !== 'AddonElement'
    ) {
      deleteButton = (
        <Button
          title="Delete"
          iconName="remove"
          key="0"
          onPress={() => this._removeField(field)}
        />
      );
    }
    return (
      <View style={styles.renderEditorStyle}>
        {deleteButton}
        <Button title="Done" iconName="check" onPress={this._closeEditor} />
      </View>
    );
  };

  _renderAddFieldMenuItems = (): React.ReactNode => {
    return formElementTypes.map((type) => {
      let menuItem = renderElementMenuItem(type);
      if (menuItem) {
        return (
          <TouchableOpacity
            activeOpacity={0.6}
            key={type}
            onPress={() => this._addField(type)}
            style={[styles.item, styles.padding]}
          >
            {menuItem}
          </TouchableOpacity>
        );
      }
      return null;
    });
  };

  _onAddFormField = () => {
    this.props.onEditGroupCancel();
  };

  _onCreateGroup = () => {
    this.props.onCreateGroupStart();
  };

  _removeGroup = (group: Group) => {
    let { editingGroup } = this.state;
    if (editingGroup && editingGroup.id === group.id) {
      this.setState({ editingGroup: null });
    }
    this.props.onRemoveGroup(group);
  };

  _startEditGroup = (group: Group) => {
    let { isAddingGroup } = this.props;
    if (isAddingGroup) {
      return;
    }
    this.setState({
      selectedFieldID: null,
      editingGroup: group,
    });
  };

  _addFieldToGroup = (field: FormField) => {
    let { editingGroup } = this.state;
    let fields = editingGroup ? editingGroup.fields : [];
    let newFields;
    if (fields?.indexOf(field.id) === -1) {
      newFields = [...fields, field.id];
    } else {
      newFields = fields?.filter((id) => id !== field.id);
    }
    this.setState({
      editingGroup: { ...editingGroup, fields: newFields },
    });
  };

  _shouldCancelSort = () => {
    let { isAddingGroup } = this.props;
    return isAddingGroup;
  };

  _onSortStart = () => {
    this._closeEditor();
  };

  _onOrderChange = (oldIndex: number, newIndex: number) => {
    let { formDef } = this.props;
    let { groups, fields } = formDef;
    let fieldSets = getFieldSets(groups || EMPTY_GROUPS, fields.toArray());
    let newFieldSets = reorderByIndex(fieldSets, oldIndex, newIndex);
    let newFields = [];
    for (let fieldSet of newFieldSets) {
      for (let field of fieldSet.children) {
        newFields.push(field);
      }
    }
    FormBuilderActions.updateForm(
      formDef,
      'fields',
      new IndexedList(newFields),
    );
  };

  _onFieldClick = (fieldID: string) => {
    let { isAddingGroup } = this.props;
    let { editingGroup } = this.state;
    if (isAddingGroup || editingGroup) {
      return;
    }
    this.setState({ selectedFieldID: fieldID });
  };

  _removeField = (field: FormField) => {
    FormBuilderActions.removeField(field);
  };

  _onGroupSave = () => {
    let editingGroup = this.state.editingGroup;
    if (editingGroup) {
      this.setState({ editingGroup: null });
      this.props.onSaveGroup(editingGroup as Group);
    }
  };

  _onGroupCancel = () => {
    this.setState({ editingGroup: null });
    this.props.onEditGroupCancel();
  };

  _closeEditor = () => {
    this.setState({ selectedFieldID: null });
  };

  _setSelectedField = (selectedFieldID: string | null) => {
    this.setState({
      selectedFieldID,
    });
  };

  _getSelectedField = (): FormField | null | undefined => {
    let selectedFieldID = this.state.selectedFieldID;
    if (selectedFieldID) {
      return this.props.formDef.fields.getByID(selectedFieldID);
    }
  };

  _documentClick = (event: { [key: string]: any }) => {
    let target: HTMLElement = event.target;
    let myRootNode = toElement(ReactDOM.findDOMNode(this));
    let appRootNode = document.getElementById('app');
    if (myRootNode && appRootNode) {
      // If the click is outside this component, but not outside the app's root node (e.g. modal dialog)
      if (appRootNode.contains(target) && !myRootNode.contains(target)) {
        this.setState({ selectedFieldID: null });
      }
    }
  };

  _onBackPress = () => this.setState({ selectedFieldID: null });

  _addField = (type: string) => {
    let { formDef } = this.props;
    let formField = createFormElement(formDef.id, type as any);
    if (formField) {
      FormBuilderActions.addField(formField);
      this.setState({ selectedFieldID: formField.id });
    }
  };
}

let styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: themeColors.backgroundGrey,
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 100,
    cursor: 'grab',
  } as any,
  toolbarContainer: {
    zIndex: 101,
    position: 'absolute',
    top: 0,
    right: 25,
    height: 30,
    flexDirection: 'row',
  },
  addFormText: {
    color: themeColors.darkGrey,
  },
  deleteButton: {
    borderWidth: 0,
    justifyContent: 'flex-start',
  },
  sortableElement: {
    flex: 1,
    justifyContent: 'flex-start',
    flexDirection: 'row',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'transparent',
  },
  groupCheckBox: {
    paddingLeft: 20,
    justifyContent: 'center',
  },
  defaultSortable: {
    flex: 1,
  },
  selectedField: {
    borderColor: themeColors.withOpacity.blue50,
    backgroundColor: themeColors.withOpacity.blue8,
    // I moved this zIndex here from above, but I don't know why we need it.
    zIndex: 102,
  },
  fieldHovered: {
    backgroundColor: themeColors.withOpacity.blue8,
  },
  isHidden: {
    cursor: 'pointer',
  } as any,
  formActionContainer: {
    paddingVertical: 5,
    paddingHorizontal: 20,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: themeColors.borderGrey,
  },
  padding: {
    padding: 10,
    paddingHorizontal: 20,
  },
  fieldContainer: {
    flex: 1,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: themeColors.borderGrey,
    justifyContent: 'space-between',
    backgroundColor: themeColors.white,
  },
  formItemContainer: {
    backgroundColor: themeColors.white,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: themeColors.borderGrey,
  },
  renderEditorStyle: {
    padding: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  itemFooter: {
    padding: 0,
  },
  fieldHeader: {
    borderBottomColor: themeColors.borderGrey,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  item: {
    borderBottomColor: themeColors.borderGrey,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  moveButton: {
    borderWidth: 0,
    backgroundColor: 'transparent',
  },
});

function createNewGroup() {
  return {
    id: createHexId(),
    title: 'New Group',
    fields: [],
  };
}

export default FieldsEditor;
